/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    Button, getPayloadObject, getTelemetryAttributes, getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent, KeyCodes, Modal,
    ModalBody, ModalHeader, onTelemetryClick, Popover, TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import { CartIconComponent, WishListIconComponent } from '@msdyn365-commerce/components';
import * as MsDyn365 from '@msdyn365-commerce/core';
import { Customer } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import { computed } from 'mobx';
import * as React from 'react';
import { HeaderPreferredStore, Logo, NavIcon } from './components';
import { IFreedomHeaderData } from './freedom-header.data';
import { IFreedomHeaderProps, IFreedomHeaderResources, IMyAccountLinksData } from './freedom-header.props.autogenerated';

export interface IHeaderState {
    mobileMenuCollapsed: boolean;
    signinPopoverOpen: boolean;
}

export interface IHeaderViewProps extends IFreedomHeaderProps<IFreedomHeaderData>, IHeaderState {
    logo: React.ReactNode;
    wishListIconDesktop: React.ReactNode;
    wishListIconMobile: React.ReactNode;
    cartIcon: React.ReactNode;
    navIcon: React.ReactNode;
    className: string;
    menuBar: React.ReactNode[];
    search: React.ReactNode[];
    siteOptions: React.ReactNode[];
    HeaderTag: IModuleProps;
    HeaderContainer: INodeProps;
    MobileMenuContainer: INodeProps;
    MobileMenuHeader: React.ReactNode;
    MobileMenuBodyContainer: INodeProps;
    MobileMenuLinksContainer: INodeProps;
    HeaderTopBarContainer: INodeProps;
    Divider: INodeProps;

    AccountInfoDropdownParentContainer?: INodeProps;
    AccountInfoDropdownPopoverConentContainer?: INodeProps;
    accountInfoDropdownButton?: React.ReactNode;

    signOutLink?: React.ReactNode;
    signInLink?: React.ReactNode;
    accountLinks?: React.ReactNode[];
    preferredStore?: React.ReactNode;
}

/**
 *
 * Header component
 * @extends {React.PureComponent<IFreedomHeaderProps<IFreedomHeaderData>>}
 */
class Header extends React.PureComponent<IFreedomHeaderProps<IFreedomHeaderData>, IHeaderState> {
    private popOverRef: React.RefObject<HTMLButtonElement>;
    private telemetryContent: ITelemetryContent;
    @computed public get displayName(): Readonly<string | undefined> {
        const customer = this.props.data.accountInformation?.result;
        return customer ? customer.FirstName || customer.Name : undefined;
    }

    constructor(props: IFreedomHeaderProps<IFreedomHeaderData>) {
        super(props);
        this.popOverRef = React.createRef();
        this._toggleNavbar = this._toggleNavbar.bind(this);
        this._togglePopover = this._togglePopover.bind(this);
        this._keydown = this._keydown.bind(this);
        this.state = {
            mobileMenuCollapsed: true,
            signinPopoverOpen: false
        };
        this.telemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!, this.props.friendlyName, this.props.telemetry);
    }

    public componentDidMount(): void {
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            window.addEventListener('keydown', this._keydown);
        }
    }

    public componentWillUnmount(): void {
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            window.removeEventListener('keydown', this._keydown, false);
        }
    }

    // tslint:disable-next-line: max-func-body-length
    public render(): JSX.Element | null {
        const {
            id,
            typeName,
            data: { accountInformation,
                cart: { result: cart },
                storeSelectorStateManager: {result: storeSelectorStateManager}
            },
            context: {
                request: {
                    user
                }
            },
            context,
            resources: {
                cartLabel,
                cartQtyLabel
            }
        } = this.props;
        const { logoLink, logoImage } = this.props.config;
        const headerClassName = classnames('ms-header', this.props.config.className);
        const navbarKey = 'header-nav-mobile';
        const customer = accountInformation && accountInformation.result;
        const cartIconSlot = this._getSlot('cartIcon');
        const menuBarSlot = this._getSlot('freedomMenuBar');
        const searchSlot = this._getSlot('search');
        const siteOptionsSlot = this._getSlot('siteOptions');
        const preferredStoreSlot = this._getSlot('storeSelector');
        const viewProps: IHeaderViewProps = {
            ...(this.props as IFreedomHeaderProps<IFreedomHeaderData>),
            ...(this.state),
            logo: <Logo {...{ link: logoLink, image: logoImage, className: 'ms-header__logo', gridSettings: this.props.context.request.gridSettings, requestContext: this.props.context.request, telemetryContent: this.telemetryContent }} />,
            wishListIconDesktop: (
                <WishListIconComponent
                    className='ms-header__wishlist-desktop'
                    showButtonTooltip={true}
                    wishlistTooltipText={this.props.resources.wishlistTooltipText}
                    context={context}
                    id={id}
                    typeName={typeName}
                    telemetryContent={this.telemetryContent}
                    data={{}}
                />
            ),
            wishListIconMobile: (
                <WishListIconComponent
                    className='ms-header__wishlist-mobile'
                    showButtonTooltip={true}
                    wishlistTooltipText={this.props.resources.wishlistTooltipText}
                    context={context}
                    id={id}
                    typeName={typeName}
                    telemetryContent={this.telemetryContent}
                    data={{}}
                />
            ),
            cartIcon: (cartIconSlot && cartIconSlot.length > 0)
                ? cartIconSlot[0]
                : cart && <CartIconComponent cartLabel={cartLabel} cartQtyLabel={cartQtyLabel} context={context} id={id} typeName={typeName} telemetryContent={this.telemetryContent} data={{ cart: cart }} />,
            navIcon: <NavIcon {...{ resources: this.props.resources, isExpanded: !this.state.mobileMenuCollapsed, targetId: navbarKey, toggleNavBar: this._toggleNavbar, telemetryContent: this.telemetryContent }} />,
            menuBar: menuBarSlot && menuBarSlot.length && menuBarSlot || [],
            search: searchSlot && searchSlot.length && searchSlot || [],
            siteOptions: siteOptionsSlot && siteOptionsSlot.length && siteOptionsSlot || [],
            className: headerClassName,
            HeaderTag: {
                moduleProps: this.props,
                className: classnames(headerClassName),
                tag: 'header'
            },
            HeaderContainer: {
                className: classnames('ms-header__container')
            },
            HeaderTopBarContainer: {
                className: classnames('ms-header__topbar freedom')
            },
            Divider: {
                className: classnames('ms-header__divider')
            },
            MobileMenuContainer: {
                tag: Modal,
                id: navbarKey,
                className: 'ms-header__mobile-hamburger',
                isOpen: !this.state.mobileMenuCollapsed,
                wrapClassName: 'ms-header__modal'
            },
            MobileMenuHeader: (
                <ModalHeader
                    className='ms-header__mobile-hamburger-menu-header'
                    toggle={this._toggleNavbar}
                />
            ),
            MobileMenuBodyContainer: {
                tag: ModalBody,
                className: 'ms-header__mobile-hamburger-menu-body',
            },
            MobileMenuLinksContainer: {
                className: 'ms-header__mobile-hamburger-menu-links',
            },
            AccountInfoDropdownParentContainer: {
                className: 'ms-header__account-info'
            },
            AccountInfoDropdownPopoverConentContainer: customer && this.displayName && {
                tag: Popover,
                id: 'myprofilePopover',
                className: 'ms-header__account-info-content',
                placement: 'bottom',
                isOpen: this.state.signinPopoverOpen,
                target: this.popOverRef,
                toggle: this._togglePopover,
            } || undefined,
            signInLink: this._getSigninButton(customer, user, this.props.resources),
            signOutLink: this._getSignOutButton(customer, user, this.props.resources),
            accountInfoDropdownButton: this._getAccountInfoDropdownButton(customer),
            accountLinks: this._getAccountLinks(customer),
            preferredStore: preferredStoreSlot
                && preferredStoreSlot.length
                && storeSelectorStateManager
                && (<HeaderPreferredStore {...this.props}/>)
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /* Handle link text change*/
    public handleLinkTextChange = (linkIndex: number) => (event: MsDyn365.ContentEditableEvent) => {
        if (this.props.config.myAccountLinks && this.props.config.myAccountLinks[linkIndex]) {
            this.props.config.myAccountLinks[linkIndex].linkText = event.target.value;
        }
    }

    private _getSlot(slotName: string): React.ReactNode[] | null {
        const { slots } = this.props;
        return (slots && slots[slotName] && slots[slotName].length && slots[slotName]) || null;
    }

    private _getSigninButton(customer: Customer | undefined, user: MsDyn365.IRequestContextUser | undefined, resources: IFreedomHeaderResources): React.ReactNode | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.SignIn);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        return !customer && (
            <Button
                className='ms-header__signin-button'
                title={resources.signInLinkText}
                href={user && user.signInUrl}
                aria-label={resources.signInLinkAriaText}
                onClick={onTelemetryClick(this.telemetryContent, payLoad, 'Sign in')}
                {...attributes}
            >
                <span className='ms-header__signin-button-text' aria-hidden={true}>{resources.signInLinkText}</span>
            </Button>
        );
    }

    private _getSignOutButton(customer: Customer | undefined, user: MsDyn365.IRequestContextUser | undefined, resources: IFreedomHeaderResources): React.ReactNode | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.SignOut);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        return customer && this.displayName && (
            <Button
                className={'ms-header__signout-button'}
                title={resources.signOutLinkText}
                href={user && user.signOutUrl || ''}
                aria-label={resources.signOutLinkAriaText}
                onClick={onTelemetryClick(this.telemetryContent, payLoad, 'Sign out')}
                {...attributes}
            >
                <span className='ms-header__signout-button-text' aria-hidden={true}>{resources.signOutLinkText}</span>
            </Button>
        );
    }

    private _getAccountInfoDropdownButton(customer: Customer | undefined): React.ReactNode | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.MyProfile);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        return customer && this.displayName && (
            <Button
                innerRef={this.popOverRef}
                className={'ms-header__profile-button'}
                aria-describedby='myprofilePopover'
                onClick={this._togglePopover}
                color='link'
                aria-expanded={this.state.signinPopoverOpen}
                {...attributes}
            >
                <span className='ms-profile-button-text'>{this.displayName}</span>
            </Button>
        );
    }

    private _getAccountLinks(customer: Customer | undefined): React.ReactNode[] | undefined {
        const { myAccountLinks } = this.props.config;
        const payLoad = getPayloadObject('click', this.telemetryContent, '');
        if(!customer || ! this.displayName || !myAccountLinks || !myAccountLinks.length) {
            return undefined;
        }
        return myAccountLinks.map((cta: IMyAccountLinksData, index: number) => {
            payLoad.contentAction.etext = cta.linkText;
            const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
            const editableLink: MsDyn365.ILinksData = {
                ariaLabel: cta.ariaLabel,
                className: 'ms-signin-info__account-link-button',
                linkText: cta.linkText,
                linkUrl: cta.linkUrl.destinationUrl,
                openInNewTab: cta.openInNewTab,
                role: 'button',
                additionalProperties: attributes
            };

            return (
                <MsDyn365.Link
                    key={index}
                    link={editableLink}
                    editProps={{ onTextChange: this.handleLinkTextChange(index), requestContext: this.props.context.request }}
                />
            );
        });
    }

    private _toggleNavbar(): void {
        this.setState({
            mobileMenuCollapsed: !this.state.mobileMenuCollapsed
        });
    }

    private _togglePopover(): void {
        this.setState({
            signinPopoverOpen: !this.state.signinPopoverOpen
        });
    }

    private _keydown(e: KeyboardEvent): void {
        if (e.keyCode === KeyCodes.Escape) {
            this.setState({ mobileMenuCollapsed: true });
        }
    }
}

export default Header;